import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Layout, Download, RotateCcw, Monitor, Database, FileSpreadsheet, Clock, User as UserIcon, School, Hash, Smartphone, CreditCard, Wallet, Search, Filter, MessageSquare, Mail, AlertTriangle, Lightbulb as LightbulbIcon, BarChart2, ShieldCheck, ShieldAlert, Globe } from 'lucide-react';
import { db, auth, doc, getDoc, setDoc, onSnapshot, collection, getDocs, query, orderBy, handleFirestoreError, OperationType } from '../src/firebase';
import { getUsageData, downloadUsageExcel, UsageData } from '../services/usageService';
import { DeductorManagement } from './DeductorManagement';
import { TabType, TreData, NavbarSettings, TreSectionData, FeedbackData, DownloadItem } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface AdminDashboardProps {
  data: TreData;
  onUpdate: (newData: TreData) => void;
  navbarSettings?: NavbarSettings;
  onUpdateNavbarSettings?: (settings: NavbarSettings) => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ data, onUpdate, navbarSettings = {}, onUpdateNavbarSettings }) => {
  const [activeSection, setActiveSection] = useState<keyof TreData | 'usage' | 'feedback' | 'navbar' | 'schoolData' | 'deductor'>('tre1');
  const [formData, setFormData] = useState<TreSectionData>(data['tre1']);
  const [isDirty, setIsDirty] = useState(false);
  const [usageLogs, setUsageLogs] = useState<UsageData[]>([]);
  const [feedbackLogs, setFeedbackLogs] = useState<FeedbackData[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [visitorStats, setVisitorStats] = useState({ total: 0, today: 0 });
  const [visitorChartData, setVisitorChartData] = useState<any[]>([]);
  const [isDbConnected, setIsDbConnected] = useState(false);
  
  useEffect(() => {
    const checkAuth = () => {
      const user = auth.currentUser;
      setIsDbConnected(!!user && user.email === 'vishal.aklavya@gmail.com');
    };
    checkAuth();
    const unsubscribe = auth.onAuthStateChanged(() => checkAuth());
    return () => unsubscribe();
  }, []);

  // New Download Item State
  const [newDownload, setNewDownload] = useState<DownloadItem>({
    title: '',
    size: '1 MB',
    type: 'pdf',
    date: new Date().toISOString().split('T')[0]
  });

  const [schoolFile, setSchoolFile] = useState<File | null>(null);
  const [isUploadingSchools, setIsUploadingSchools] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const handleSchoolFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSchoolFile(e.target.files[0]);
      setUploadMessage(null);
    }
  };

  const submitSchoolFile = async () => {
    if (!schoolFile) return;
    
    setIsUploadingSchools(true);
    setUploadMessage(null);
    
    const formData = new FormData();
    formData.append('file', schoolFile);
    
    try {
      const response = await fetch('/api/upload-schools', {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (result.success) {
        setUploadMessage({ type: 'success', text: `Successfully uploaded ${result.count} schools!` });
        setSchoolFile(null);
      } else {
        setUploadMessage({ type: 'error', text: result.error || 'Failed to upload schools' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadMessage({ type: 'error', text: 'An error occurred during upload' });
    } finally {
      setIsUploadingSchools(false);
    }
  };

  // Update local form when active section changes
  useEffect(() => {
    if (activeSection !== 'usage' && activeSection !== 'feedback' && activeSection !== 'navbar' && activeSection !== 'schoolData') {
      setFormData(data[activeSection as keyof TreData]);
      setIsDirty(false);
    } else if (activeSection === 'usage') {
      fetchLogs();
      fetchChartData();
    } else if (activeSection === 'feedback') {
      fetchFeedback();
    }
  }, [activeSection, data]);

  const fetchChartData = async () => {
    try {
      const statsSnap = await getDoc(doc(db, 'stats', 'visitors'));
      if (statsSnap.exists()) {
        setVisitorChartData(statsSnap.data().chartData || []);
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, 'stats/visitors');
    }
  };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'stats', 'visitors'), (snapshot: any) => {
      if (snapshot.exists()) {
        setVisitorStats(snapshot.data() as any);
      }
    }, (error: any) => {
      handleFirestoreError(error, OperationType.GET, 'stats/visitors');
    });
    return () => unsub();
  }, []);

  const fetchLogs = async () => {
    setLoadingLogs(true);
    const logs = await getUsageData();
    setUsageLogs(logs);
    setLoadingLogs(false);
  };

  const fetchFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const q = query(collection(db, 'feedback'), orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      const logs = querySnapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id })) as FeedbackData[];
      setFeedbackLogs(logs);
    } catch (error: any) {
      handleFirestoreError(error, OperationType.LIST, 'feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const filteredLogs = usageLogs.filter(log => {
    const matchesSearch = 
      (log.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
       log.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       log.udiseCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       log.mobileNumber?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSection = sectionFilter === 'all' || log.section === sectionFilter;
    
    return matchesSearch && matchesSection;
  });

  const sections = Array.from(new Set(usageLogs.map(l => l.section)));

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setIsDirty(true);
  };

  const handleBannerChange = (field: string, value: string) => {
    if (!formData.customBanner) return;
    setFormData({
      ...formData,
      customBanner: {
        ...formData.customBanner,
        [field]: value
      }
    });
    setIsDirty(true);
  };

  const handleDeleteDownload = (index: number) => {
    const updatedDownloads = formData.downloads.filter((_, i) => i !== index);
    setFormData({ ...formData, downloads: updatedDownloads });
    setIsDirty(true);
  };

  const handleAddDownload = () => {
    if (!newDownload.title) return;
    const updatedDownloads = [...formData.downloads, newDownload];
    setFormData({ ...formData, downloads: updatedDownloads });
    setNewDownload({
      title: '',
      size: '1 MB',
      type: 'pdf',
      date: new Date().toISOString().split('T')[0]
    });
    setIsDirty(true);
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to discard unsaved changes?")) {
      if (activeSection !== 'usage' && activeSection !== 'feedback' && activeSection !== 'navbar') {
        setFormData(data[activeSection as keyof TreData]);
      }
      setIsDirty(false);
    }
  };

  const saveChanges = async () => {
    if (activeSection === 'navbar') {
      alert('Navbar settings are saved automatically.');
      return;
    }
    try {
      const updatedData = {
        ...data,
        [activeSection]: formData
      };
      await setDoc(doc(db, 'settings', 'resources'), updatedData);
      onUpdate(updatedData);
      setIsDirty(false);
      alert('Changes saved successfully!');
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'settings/resources');
      alert('Failed to save changes.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-900 p-6 flex flex-col md:flex-row justify-between items-center text-white gap-4">
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold flex items-center">
              <Layout className="mr-3" /> Admin Dashboard
            </h2>
            <div className="flex items-center mt-1">
              {isDbConnected ? (
                <span className="flex items-center text-[10px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full border border-emerald-400/20">
                  <Globe size={10} className="mr-1" /> Database Connected
                </span>
              ) : (
                <span className="flex items-center text-[10px] font-black uppercase tracking-widest text-red-400 bg-red-400/10 px-2 py-0.5 rounded-full border border-red-400/20">
                  <ShieldAlert size={10} className="mr-1" /> Database Disconnected
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 items-center">
            <div className="flex items-center space-x-4 text-sm bg-gray-800 px-4 py-2 rounded-lg border border-gray-700">
              <div className="flex items-center text-emerald-400" title="Today's Visitors">
                <UserIcon size={16} className="mr-2" />
                <span className="font-semibold">Today: {visitorStats.today}</span>
              </div>
              <div className="w-px h-4 bg-gray-600"></div>
              <div className="flex items-center text-blue-400" title="Total Visitors">
                <UserIcon size={16} className="mr-2" />
                <span className="font-semibold">Total: {visitorStats.total}</span>
              </div>
            </div>
             {isDirty && (
              <button 
                onClick={handleReset}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center transition"
              >
                <RotateCcw size={18} className="mr-2" /> Reset
              </button>
             )}
            <button 
              onClick={saveChanges}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center transition shadow-lg"
            >
              <Save size={18} className="mr-2" /> Save All Changes
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 min-h-[600px]">
          {/* Sidebar */}
          <div className="bg-gray-50 border-r border-gray-200 p-3 sm:p-4 overflow-x-auto lg:overflow-x-visible">
            <h3 className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 sm:mb-4">Select Section</h3>
            <div className="flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2 pb-2 lg:pb-0">
              <button
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm("You have unsaved changes. Switch anyway?")) setActiveSection('usage');
                  } else {
                    setActiveSection('usage');
                  }
                }}
                className={`flex-shrink-0 lg:w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition flex justify-between items-center text-xs sm:text-sm ${
                  activeSection === 'usage' 
                    ? 'bg-white text-indigo-600 shadow-sm border border-indigo-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <Database size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" /> USAGE LOGS
                </div>
                {activeSection === 'usage' && <Monitor size={14} className="hidden sm:block" />}
              </button>

              <button
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm("You have unsaved changes. Switch anyway?")) setActiveSection('feedback');
                  } else {
                    setActiveSection('feedback');
                  }
                }}
                className={`flex-shrink-0 lg:w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition flex justify-between items-center text-xs sm:text-sm ${
                  activeSection === 'feedback' 
                    ? 'bg-white text-orange-600 shadow-sm border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <MessageSquare size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" /> USER FEEDBACK
                </div>
                {activeSection === 'feedback' && <Monitor size={14} className="hidden sm:block" />}
              </button>

              <button
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm("You have unsaved changes. Switch anyway?")) setActiveSection('navbar');
                  } else {
                    setActiveSection('navbar');
                  }
                }}
                className={`flex-shrink-0 lg:w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition flex justify-between items-center text-xs sm:text-sm ${
                  activeSection === 'navbar' 
                    ? 'bg-white text-emerald-600 shadow-sm border border-emerald-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <Layout size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" /> NAVBAR SETTINGS
                </div>
                {activeSection === 'navbar' && <Monitor size={14} className="hidden sm:block" />}
              </button>

              <button
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm("You have unsaved changes. Switch anyway?")) setActiveSection('schoolData');
                  } else {
                    setActiveSection('schoolData');
                  }
                }}
                className={`flex-shrink-0 lg:w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition flex justify-between items-center text-xs sm:text-sm ${
                  activeSection === 'schoolData' 
                    ? 'bg-white text-blue-600 shadow-sm border border-blue-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <Database size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" /> SCHOOL DATA
                </div>
                {activeSection === 'schoolData' && <Monitor size={14} className="hidden sm:block" />}
              </button>

              <button
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm("You have unsaved changes. Switch anyway?")) setActiveSection('deductor');
                  } else {
                    setActiveSection('deductor');
                  }
                }}
                className={`flex-shrink-0 lg:w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition flex justify-between items-center text-xs sm:text-sm ${
                  activeSection === 'deductor' 
                    ? 'bg-white text-blue-600 shadow-sm border border-blue-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <Database size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" /> DEDUCTOR DATA
                </div>
                {activeSection === 'deductor' && <Monitor size={14} className="hidden sm:block" />}
              </button>

              {(Object.keys(data) as Array<keyof TreData>).map((key) => (
                <button
                  key={key}
                  onClick={() => {
                    if (isDirty) {
                      if (window.confirm("You have unsaved changes. Switch anyway?")) setActiveSection(key);
                    } else {
                      setActiveSection(key);
                    }
                  }}
                  className={`flex-shrink-0 lg:w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition flex justify-between items-center text-xs sm:text-sm ${
                    activeSection === key 
                      ? 'bg-white text-orange-600 shadow-sm border border-orange-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <Database size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" />
                    {key === 'bpscTre3' ? 'BPSC TRE 3.0' : key === 'automaticArrear' ? 'AREAR AUTOMATIC' : (key as string).toUpperCase()}
                  </div>
                  {activeSection === key && <Monitor size={14} className="hidden sm:block" />}
                </button>
              ))}
            </div>
          </div>

          {/* Main Edit Area */}
          <div className="col-span-1 lg:col-span-3 p-6 lg:p-8 overflow-y-auto max-h-[800px]">
            {!isDbConnected && (
              <div className="mb-8 p-4 bg-red-50 border-2 border-red-100 rounded-2xl flex items-start animate-pulse">
                <div className="bg-red-100 p-2 rounded-xl mr-4 text-red-600">
                  <ShieldAlert size={24} />
                </div>
                <div>
                  <h4 className="font-black text-red-900 uppercase tracking-tight">डेटाबेस कनेक्शन एरर!</h4>
                  <p className="text-red-700 text-sm font-medium leading-relaxed">
                    आप अभी "Google Login" के जरिए कनेक्टेड नहीं हैं। आप बदलाव देख सकते हैं, लेकिन उन्हें **Save** नहीं कर पाएंगे। 
                    कृपया "Sign in with Google" का उपयोग करें।
                  </p>
                </div>
              </div>
            )}

            {activeSection === 'navbar' ? (
              <div className="animate-fadeIn">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">Navbar Visibility Settings</h3>
                    <p className="text-gray-500 text-sm font-medium">Toggle visibility of specific sections in the navigation bar</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 space-y-4">
                    {[
                      { id: 'home', label: 'Home' },
                      { id: 'tre1', label: 'AREAR' },
                      { id: 'autoArrearExperiment', label: 'AREAR AUTOMATIC' },
                      { id: 'tre2', label: 'TDS' },
                      { id: 'salary', label: 'सैलरी जानें' },
                      { id: 'absenty', label: 'ABSENTY' },
                      { id: 'brc', label: 'BRC' },
                      { id: 'bpscTre3', label: 'ASSET AND LIABILITY' },
                      { id: 'school', label: 'SCHOOL' },
                    ].map((item) => {
                      const isVisible = navbarSettings[item.id as TabType] !== false;
                      return (
                        <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div>
                            <h4 className="font-bold text-gray-900">{item.label}</h4>
                            <p className="text-xs text-gray-500">Identifier: {item.id}</p>
                          </div>
                          <button
                            onClick={async () => {
                              const newSettings = { ...navbarSettings, [item.id]: !isVisible };
                              if (onUpdateNavbarSettings) {
                                onUpdateNavbarSettings(newSettings);
                              }
                              try {
                                await setDoc(doc(db, 'settings', 'navbar'), newSettings);
                              } catch (e) {
                                handleFirestoreError(e, OperationType.WRITE, 'settings/navbar');
                              }
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${isVisible ? 'bg-orange-600' : 'bg-gray-200'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isVisible ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : activeSection === 'usage' ? (
              <div className="animate-fadeIn">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">User Usage Logs</h3>
                    <p className="text-gray-500 text-sm font-medium">Track website activity and PDF generations</p>
                  </div>
                  <div className="flex space-x-3">
                    <button 
                      onClick={() => fetchLogs()}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl font-bold text-sm flex items-center transition"
                    >
                      <RotateCcw size={16} className={`mr-2 ${loadingLogs ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                    <button 
                      onClick={() => downloadUsageExcel(sectionFilter)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center transition shadow-lg shadow-emerald-100"
                    >
                      <FileSpreadsheet size={16} className="mr-2" /> Export Excel
                    </button>
                  </div>
                </div>

                {visitorChartData.length > 0 && (
                  <div className="bg-white rounded-[2rem] border border-gray-200 shadow-sm p-6 mb-8">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <BarChart2 className="mr-2 text-indigo-500" /> Visitor Trends (Last 30 Days)
                    </h4>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={visitorChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                          <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
                            cursor={{ fill: '#F3F4F6' }}
                          />
                          <Bar dataKey="count" name="Visitors" fill="#6366F1" radius={[4, 4, 0, 0]} barSize={30} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                      type="text"
                      placeholder="Search name, school, udise..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition"
                    />
                  </div>
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select 
                      value={sectionFilter}
                      onChange={(e) => setSectionFilter(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition appearance-none"
                    >
                      <option value="all">All Sections</option>
                      {sections.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="bg-indigo-50 rounded-2xl px-6 py-3 flex items-center justify-between border border-indigo-100">
                    <span className="text-indigo-700 font-bold text-sm">Total Records</span>
                    <span className="text-2xl font-black text-indigo-900">{filteredLogs.length}</span>
                  </div>
                </div>

                <div className="bg-white rounded-[2rem] border border-gray-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Date & Time</th>
                          <th className="px-6 py-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Section</th>
                          <th className="px-6 py-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">User Details</th>
                          <th className="px-6 py-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">School & UDISE</th>
                          <th className="px-6 py-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loadingLogs ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-20 text-center">
                              <div className="flex flex-col items-center">
                                <RotateCcw className="animate-spin text-indigo-600 mb-4" size={40} />
                                <p className="text-gray-500 font-bold">Loading usage logs...</p>
                              </div>
                            </td>
                          </tr>
                        ) : filteredLogs.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-20 text-center text-gray-500 font-bold">No logs found matching your criteria.</td>
                          </tr>
                        ) : (
                          filteredLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50 transition">
                              <td className="px-6 py-4">
                                <div className="flex items-center text-gray-900 font-bold">
                                  <Clock size={14} className="mr-2 text-gray-400" />
                                  {new Date(log.timestamp || '').toLocaleString()}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                  log.section.includes('Arrear') ? 'bg-orange-100 text-orange-700' : 
                                  log.section.includes('Salary') ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {log.section}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="space-y-1">
                                  <div className="flex items-center text-gray-900 font-bold">
                                    <UserIcon size={14} className="mr-2 text-indigo-500" />
                                    {log.name || 'N/A'}
                                  </div>
                                  <div className="flex items-center text-gray-500 text-xs font-medium">
                                    <Smartphone size={12} className="mr-2" />
                                    {log.mobileNumber || 'N/A'}
                                  </div>
                                  <div className="flex items-center text-gray-500 text-xs font-medium">
                                    <CreditCard size={12} className="mr-2" />
                                    PAN: {log.panNumber || 'N/A'}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="space-y-1">
                                  <div className="flex items-center text-gray-900 font-bold">
                                    <School size={14} className="mr-2 text-emerald-500" />
                                    {log.schoolName || 'N/A'}
                                  </div>
                                  <div className="flex items-center text-gray-500 text-xs font-medium">
                                    <Hash size={12} className="mr-2" />
                                    UDISE: {log.udiseCode || 'N/A'}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center text-indigo-600 font-black text-lg">
                                  <Wallet size={16} className="mr-2" />
                                  ₹{log.amount || '0'}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : activeSection === 'deductor' ? (
              <div className="animate-fadeIn p-6">
                <DeductorManagement />
              </div>
            ) : activeSection === 'schoolData' ? (
              <div className="animate-fadeIn">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">School Data Management</h3>
                    <p className="text-gray-500 text-sm font-medium">Upload and manage school data for the Search School feature</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                  <div className="max-w-2xl">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">Upload School Data</h4>
                    <p className="text-gray-600 mb-6 text-sm">
                      Upload a JSON or TXT file containing an array of school objects. The file should be formatted as a JSON array with objects containing "UDIES CODE", "SCHOOL NAME", "BLOCK", and "DISTRICT" properties.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:bg-gray-50 transition-colors">
                        <input
                          type="file"
                          accept=".json,.txt"
                          onChange={handleSchoolFileUpload}
                          className="hidden"
                          id="school-file-upload"
                        />
                        <label
                          htmlFor="school-file-upload"
                          className="cursor-pointer flex flex-col items-center justify-center"
                        >
                          <Database className="w-10 h-10 text-blue-500 mb-3" />
                          <span className="text-sm font-medium text-gray-700">
                            {schoolFile ? schoolFile.name : 'Click to select a file (.json or .txt)'}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            {schoolFile ? `${(schoolFile.size / 1024).toFixed(2)} KB` : 'Max file size: 50MB'}
                          </span>
                        </label>
                      </div>

                      {uploadMessage && (
                        <div className={`p-4 rounded-lg text-sm font-medium ${
                          uploadMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {uploadMessage.text}
                        </div>
                      )}

                      <button
                        onClick={submitSchoolFile}
                        disabled={!schoolFile || isUploadingSchools}
                        className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                      >
                        {isUploadingSchools ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            UPLOADING...
                          </>
                        ) : (
                          'UPLOAD SCHOOL DATA'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeSection === 'feedback' ? (
              <div className="animate-fadeIn">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">User Feedback</h3>
                    <p className="text-gray-500 text-sm font-medium">Suggestions and bug reports from users</p>
                  </div>
                  <button 
                    onClick={() => fetchFeedback()}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl font-bold text-sm flex items-center transition"
                  >
                    <RotateCcw size={16} className={`mr-2 ${loadingFeedback ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-2xl px-6 py-3 flex items-center justify-between border border-orange-100">
                    <span className="text-orange-700 font-bold text-sm">Total Feedback</span>
                    <span className="text-2xl font-black text-orange-900">{feedbackLogs.length}</span>
                  </div>
                  <div className="bg-blue-50 rounded-2xl px-6 py-3 flex items-center justify-between border border-blue-100">
                    <span className="text-blue-700 font-bold text-sm">Suggestions</span>
                    <span className="text-2xl font-black text-blue-900">{feedbackLogs.filter(f => f.type === 'suggestion').length}</span>
                  </div>
                  <div className="bg-red-50 rounded-2xl px-6 py-3 flex items-center justify-between border border-red-100">
                    <span className="text-red-700 font-bold text-sm">Bugs</span>
                    <span className="text-2xl font-black text-red-900">{feedbackLogs.filter(f => f.type === 'bug').length}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {loadingFeedback ? (
                    <div className="bg-white rounded-[2rem] border border-gray-200 p-20 text-center">
                      <div className="flex flex-col items-center">
                        <RotateCcw className="animate-spin text-orange-600 mb-4" size={40} />
                        <p className="text-gray-500 font-bold">Loading feedback...</p>
                      </div>
                    </div>
                  ) : feedbackLogs.length === 0 ? (
                    <div className="bg-white rounded-[2rem] border border-gray-200 p-20 text-center text-gray-500 font-bold">
                      No feedback received yet.
                    </div>
                  ) : (
                    feedbackLogs.slice().reverse().map((feedback) => (
                      <div key={feedback.id} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center">
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center ${
                              feedback.type === 'suggestion' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {feedback.type === 'suggestion' ? <LightbulbIcon size={12} className="mr-1" /> : <AlertTriangle size={12} className="mr-1" />}
                              {feedback.type}
                            </span>
                            <span className="ml-3 text-xs text-gray-400 font-medium flex items-center">
                              <Clock size={12} className="mr-1" />
                              {new Date(feedback.timestamp).toLocaleString()}
                            </span>
                          </div>
                          {feedback.email && (
                            <div className="flex items-center text-xs text-gray-500 font-medium bg-gray-50 px-3 py-1 rounded-lg">
                              <Mail size={12} className="mr-1.5 text-gray-400" />
                              {feedback.email}
                            </div>
                          )}
                        </div>
                        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap font-medium">
                          {feedback.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-800 mb-6 border-b pb-4 flex items-center">
                  Editing: <span className="text-orange-600 ml-2">{(activeSection as string).toUpperCase()}</span>
                </h3>
            
            {/* General Info */}
            <div className="space-y-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleTextChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleTextChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>
            </div>

            {/* Banner Config - Only if valid for this section */}
            {formData.customBanner && (
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-8">
                 <h4 className="text-blue-800 font-bold mb-3 flex items-center">
                    <Monitor size={18} className="mr-2" /> Banner Configuration
                 </h4>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-blue-700 mb-1">Banner Text</label>
                      <input
                        value={formData.customBanner.text}
                        onChange={(e) => handleBannerChange('text', e.target.value)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-blue-700 mb-1">Button Label</label>
                      <input
                        value={formData.customBanner.buttonText}
                        onChange={(e) => handleBannerChange('buttonText', e.target.value)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-blue-700 mb-1">Sub Text</label>
                      <input
                        value={formData.customBanner.subText || ''}
                        onChange={(e) => handleBannerChange('subText', e.target.value)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                      />
                    </div>
                 </div>
              </div>
            )}

            {/* Downloads Management */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Download size={20} className="mr-2" /> Manage Downloads
              </h4>
              
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden mb-6">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-100 text-gray-600 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 font-medium">Title</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {formData.downloads.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-4 text-center text-gray-500">No downloads available. Add one below.</td>
                      </tr>
                    ) : (
                      formData.downloads.map((item: any, idx: number) => (
                        <tr key={idx} className="bg-white hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{item.title}</td>
                          <td className="px-4 py-3 text-gray-500">{item.date}</td>
                          <td className="px-4 py-3">
                            <button 
                              onClick={() => handleDeleteDownload(idx)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition"
                              title="Delete File"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Add New */}
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h5 className="font-semibold text-orange-800 mb-3 flex items-center">
                  <Plus size={16} className="mr-1" /> Add New File
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-5">
                    <input
                      placeholder="File Title"
                      value={newDownload.title}
                      onChange={(e) => setNewDownload({...newDownload, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                     <select 
                       value={newDownload.type}
                       onChange={(e) => setNewDownload({...newDownload, type: e.target.value as any})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-1 focus:ring-orange-500 outline-none"
                     >
                       <option value="pdf">PDF</option>
                       <option value="doc">DOC</option>
                       <option value="link">LINK</option>
                     </select>
                  </div>
                  <div className="md:col-span-2">
                    <input
                      placeholder="Size"
                      value={newDownload.size}
                      onChange={(e) => setNewDownload({...newDownload, size: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-orange-500 outline-none"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <button 
                      onClick={handleAddDownload}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 rounded-lg text-sm transition shadow-sm"
                    >
                      Add File
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        </div>
        </div>
      </div>
    </div>
  );
};